import { Outlet, useNavigate, useRouter } from '@tanstack/react-router'
import { type ReactNode, useEffect } from 'react'

import { type System, useAuth } from '@/lib/auth-store'
import { loginPathFor, loginSearchFor } from '@/lib/return-path'

/**
 * Guarded layout. The route's `beforeLoad` handles the first visit; this
 * handles a session dying mid-visit (a 401 cleared the store) — same
 * redirect, same return path, and the login page is never itself guarded.
 */
export function RequireAuth({ system, children }: { system: System; children?: ReactNode }) {
  const auth = useAuth(system)
  const authenticated = auth.status === 'authenticated'
  const router = useRouter()
  const navigate = useNavigate()

  // Fires once per auth loss: the location is read here, not depended on,
  // because this tree stays mounted while the transition to login is pending.
  useEffect(() => {
    if (authenticated) return
    const search = loginSearchFor(system, router.state.location)
    void navigate({ to: loginPathFor(system), search, replace: true })
  }, [authenticated, navigate, router, system])

  if (!authenticated) return null
  return children ?? <Outlet />
}
